import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  EmojiEvents as TrophyIcon,
  Person as PersonIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, doc, getDoc, getDocs, query, orderBy } from 'firebase/firestore'
import toast from 'react-hot-toast'
import SpaceTheme from '../components/SpaceTheme'

const Leaderboard = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [userScores, setUserScores] = useState([])
  const [currentUserScore, setCurrentUserScore] = useState(null)
  const [loading, setLoading] = useState(true)
  const [currentLevel, setCurrentLevel] = useState('A1')

  useEffect(() => {
    if (user && userData) {
      setCurrentLevel(userData.level || 'A1')
      loadLeaderboard(userData.level || 'A1')
    }
  }, [user, userData])

  const loadLeaderboard = async (level) => {
    try {
      setLoading(true)
      console.log('Loading leaderboard for level:', level)

      // Seviyeye özel leaderboard'u yükle
      const leaderboardRef = collection(db, 'levels', level, 'leaderboard')
      const q = query(leaderboardRef, orderBy('totalScore', 'desc'))
      const querySnapshot = await getDocs(q)

      const scores = []
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        const username = data.username
        const matchScore = data.matchScore || 0
        const scrambleScore = data.scrambleScore || 0
        const quizScore = data.quizScore || 0
        const listeningWriteScore = data.listeningWriteScore || 0
        const savedWordsScore = data.savedWordsScore || 0
        
        // Android uygulamasındaki gibi totalScore hesapla
        const totalScore = matchScore + scrambleScore + quizScore + listeningWriteScore + savedWordsScore

        if (username) {
          scores.push({
            id: doc.id,
            username,
            matchScore,
            scrambleScore,
            quizScore,
            listeningWriteScore,
            savedWordsScore,
            totalScore
          })
        }
      })

      console.log('Leaderboard loaded:', scores.length, 'scores')
      setUserScores(scores)

      // Kullanıcının kendi skorunu al
      if (user) {
        const userScoreDoc = doc(db, 'levels', level, 'leaderboard', user.uid)
        const userScoreSnapshot = await getDoc(userScoreDoc)
        
                         if (userScoreSnapshot.exists()) {
          const data = userScoreSnapshot.data()
          const matchScore = data.matchScore || 0
          const scrambleScore = data.scrambleScore || 0
          const quizScore = data.quizScore || 0
          const listeningWriteScore = data.listeningWriteScore || 0
          const savedWordsScore = data.savedWordsScore || 0
          
          // Android uygulamasındaki gibi totalScore hesapla
          const totalScore = matchScore + scrambleScore + quizScore + listeningWriteScore + savedWordsScore
          
          setCurrentUserScore({
            matchScore,
            scrambleScore,
            quizScore,
            listeningWriteScore,
            savedWordsScore,
            totalScore
          })
        }
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error)
      toast.error('Leaderboard yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const getRankIcon = (rank) => {
    if (rank === 1) return '🏆'
    if (rank === 2) return '🥈'
    if (rank === 3) return '🥉'
    return `#${rank}`
  }

  const getRankColor = (rank) => {
    if (rank === 1) return '#FFD700' // Gold
    if (rank === 2) return '#C0C0C0' // Silver
    if (rank === 3) return '#CD7F32' // Bronze
    return '#1976d2'
  }

  const getRankBackground = (rank) => {
    if (rank === 1) return '#FFF8DC'
    if (rank === 2) return '#F5F5F5'
    if (rank === 3) return '#FDF5E6'
    return 'white'
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Leaderboard
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card - Reading sayfasındaki gibi */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <TrophyIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              {currentLevel} Leaderboard
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8 }}>
              En yüksek puanlı öğrenciler
            </Typography>
          </CardContent>
        </Card>

                 {/* Current User Score Card */}
         {currentUserScore && (
           <Card sx={{ 
             mb: 3, 
             mx: 2, 
             background: 'linear-gradient(135deg, #f3f8ff 0%, #e3f2fd 100%)',
             border: '2px solid #1976d2',
             borderRadius: 3,
             boxShadow: 4
           }}>
             <CardContent sx={{ p: 3 }}>
               <Typography variant="h5" gutterBottom sx={{ 
                 color: '#1976d2', 
                 fontWeight: 'bold',
                 textAlign: 'center',
                 mb: 3
               }}>
                 🏆 Sizin Skorunuz
               </Typography>
               <Grid container spacing={3}>
                 <Grid item xs={6} sm={4}>
                   <Card sx={{ 
                     backgroundColor: 'white', 
                     borderRadius: 2,
                     boxShadow: 2,
                     p: 2,
                     textAlign: 'center'
                   }}>
                     <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Match</Typography>
                     <Typography variant="h4" color="primary" fontWeight="bold">{currentUserScore.matchScore}</Typography>
                   </Card>
                 </Grid>
                 <Grid item xs={6} sm={4}>
                   <Card sx={{ 
                     backgroundColor: 'white', 
                     borderRadius: 2,
                     boxShadow: 2,
                     p: 2,
                     textAlign: 'center'
                   }}>
                     <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Scramble</Typography>
                     <Typography variant="h4" color="primary" fontWeight="bold">{currentUserScore.scrambleScore}</Typography>
                   </Card>
                 </Grid>
                 <Grid item xs={6} sm={4}>
                   <Card sx={{ 
                     backgroundColor: 'white', 
                     borderRadius: 2,
                     boxShadow: 2,
                     p: 2,
                     textAlign: 'center'
                   }}>
                     <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Quiz</Typography>
                     <Typography variant="h4" color="primary" fontWeight="bold">{currentUserScore.quizScore}</Typography>
                   </Card>
                 </Grid>
                 <Grid item xs={6} sm={4}>
                   <Card sx={{ 
                     backgroundColor: 'white', 
                     borderRadius: 2,
                     boxShadow: 2,
                     p: 2,
                     textAlign: 'center'
                   }}>
                     <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Listening</Typography>
                     <Typography variant="h4" color="primary" fontWeight="bold">{currentUserScore.listeningWriteScore}</Typography>
                   </Card>
                 </Grid>
                 <Grid item xs={6} sm={4}>
                   <Card sx={{ 
                     backgroundColor: 'white', 
                     borderRadius: 2,
                     boxShadow: 2,
                     p: 2,
                     textAlign: 'center'
                   }}>
                     <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Saved Words</Typography>
                     <Typography variant="h4" color="primary" fontWeight="bold">{currentUserScore.savedWordsScore}</Typography>
                   </Card>
                 </Grid>
                 <Grid item xs={12}>
                   <Card sx={{ 
                     background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                     borderRadius: 3,
                     boxShadow: 4,
                     p: 3,
                     textAlign: 'center'
                   }}>
                     <Typography variant="h6" color="white" sx={{ mb: 1 }}>🎯 Toplam Puan</Typography>
                     <Typography variant="h2" color="white" fontWeight="bold">
                       {currentUserScore.totalScore}
                     </Typography>
                   </Card>
                 </Grid>
               </Grid>
             </CardContent>
           </Card>
         )}

        {/* Leaderboard List */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        ) : userScores.length === 0 ? (
          <Card sx={{ mx: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <TrophyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Henüz Skor Yok
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bu seviye için henüz skor bulunmuyor
              </Typography>
            </CardContent>
          </Card>
                 ) : (
           <Card sx={{ 
             mx: 2, 
             borderRadius: 3,
             boxShadow: 4,
             background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
           }}>
             <CardContent sx={{ p: 3 }}>
               <Typography variant="h5" gutterBottom sx={{ 
                 color: '#1976d2', 
                 fontWeight: 'bold',
                 textAlign: 'center',
                 mb: 3
               }}>
                 🏅 Sıralama ({userScores.length} öğrenci)
               </Typography>
               <List>
                 {userScores.map((score, index) => (
                   <ListItem 
                     key={score.id} 
                     sx={{ 
                       border: '2px solid #e0e0e0', 
                       mb: 2, 
                       borderRadius: 3,
                       backgroundColor: getRankBackground(index + 1),
                       boxShadow: 3,
                       transition: 'all 0.3s ease',
                       '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: 6
                       }
                     }}
                   >
                     <ListItemAvatar>
                       <Avatar sx={{ 
                         backgroundColor: getRankColor(index + 1),
                         color: 'white',
                         fontWeight: 'bold',
                         width: 56,
                         height: 56,
                         fontSize: '1.2rem',
                         boxShadow: 3
                       }}>
                         {getRankIcon(index + 1)}
                       </Avatar>
                     </ListItemAvatar>
                     <ListItemText
                       primary={
                         <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                           {score.username}
                         </Typography>
                       }
                       secondary={
                         <Box>
                           <Grid container spacing={2} sx={{ mt: 1 }}>
                             <Grid item xs={6} sm={4}>
                               <Chip 
                                 label={`Match: ${score.matchScore}`} 
                                 size="medium" 
                                 variant="outlined"
                                 sx={{ 
                                   fontSize: '0.8rem',
                                   fontWeight: 'bold',
                                   borderColor: '#1976d2',
                                   color: '#1976d2'
                                 }}
                               />
                             </Grid>
                             <Grid item xs={6} sm={4}>
                               <Chip 
                                 label={`Scramble: ${score.scrambleScore}`} 
                                 size="medium" 
                                 variant="outlined"
                                 sx={{ 
                                   fontSize: '0.8rem',
                                   fontWeight: 'bold',
                                   borderColor: '#1976d2',
                                   color: '#1976d2'
                                 }}
                               />
                             </Grid>
                             <Grid item xs={6} sm={4}>
                               <Chip 
                                 label={`Quiz: ${score.quizScore}`} 
                                 size="medium" 
                                 variant="outlined"
                                 sx={{ 
                                   fontSize: '0.8rem',
                                   fontWeight: 'bold',
                                   borderColor: '#1976d2',
                                   color: '#1976d2'
                                 }}
                               />
                             </Grid>
                             <Grid item xs={6} sm={4}>
                               <Chip 
                                 label={`Listening: ${score.listeningWriteScore}`} 
                                 size="medium" 
                                 variant="outlined"
                                 sx={{ 
                                   fontSize: '0.8rem',
                                   fontWeight: 'bold',
                                   borderColor: '#1976d2',
                                   color: '#1976d2'
                                 }}
                               />
                             </Grid>
                             <Grid item xs={6} sm={4}>
                               <Chip 
                                 label={`Saved: ${score.savedWordsScore}`} 
                                 size="medium" 
                                 variant="outlined"
                                 sx={{ 
                                   fontSize: '0.8rem',
                                   fontWeight: 'bold',
                                   borderColor: '#1976d2',
                                   color: '#1976d2'
                                 }}
                               />
                             </Grid>
                           </Grid>
                                      <Card sx={{ 
             mt: 2,
             background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
             borderRadius: 2,
             boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
             backdropFilter: 'blur(10px)'
           }}>
                             <CardContent sx={{ p: 2, textAlign: 'center' }}>
                               <Typography variant="h5" color="white" fontWeight="bold">
                                 🎯 Toplam: {score.totalScore}
                               </Typography>
                             </CardContent>
                           </Card>
                         </Box>
                       }
                     />
                   </ListItem>
                 ))}
               </List>
             </CardContent>
           </Card>
         )}

                 {/* Info Alert */}
         <Alert severity="info" sx={{ 
           mt: 3, 
           mx: 2, 
           borderRadius: 3,
           background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
           border: '2px solid #1976d2'
         }}>
           <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
             💡 Leaderboard'da seviyenize göre en yüksek puanlı öğrencileri görebilirsiniz. 
             Oyunlar oynayarak ve kelimeler çalışarak puanınızı artırabilirsiniz!
           </Typography>
         </Alert>
      </Container>
    </SpaceTheme>
  )
}

export default Leaderboard
